/* ═══════════════════════════════════════════════════════════════
   Meter — planned vs actual vs remaining. Label row + bar (actual over
   planned, over-run hatched in --color-danger) + a value row of
   kit-formatted strings the caller already produced (fmtMoney/fmtHours/
   etc. live page-side — this file never formats a number itself). An
   optional `rows` list renders per-phase stacked meters when wide.
   ═══════════════════════════════════════════════════════════════ */

import React from 'react';
import { cn } from '../../lib/utils';
import { rem } from '../../lib/rem';
import { toneToVar, type ChartTone } from './WChart';

function barColor(pct: number): string {
  if (pct > 100) return 'var(--color-danger)';
  if (pct > 90) return 'var(--color-warning)';
  return 'var(--color-success)';
}

function Bar({ pct, height = 8, tone }: { pct: number; height?: number; tone?: ChartTone }) {
  const color = tone ? toneToVar(tone) : barColor(pct);
  return (
    <div
      style={{
        height: rem(height),
        background: 'var(--sa)',
        borderRadius: 'var(--radius-sm)',
        overflow: 'hidden',
        position: 'relative',
      }}
    >
      <div
        style={{
          width: '100%',
          height: '100%',
          background: color,
          transform: `scaleX(${Math.min(100, pct) / 100})`,
          transformOrigin: 'left',
          transition: 'transform var(--duration-slow) var(--ease-out)',
        }}
      />
      {pct > 100 && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background:
              'repeating-linear-gradient(45deg, transparent 0 6px, color-mix(in srgb, var(--color-danger) 25%, transparent) 6px 10px)',
          }}
        />
      )}
    </div>
  );
}

/** Minimum width (rem) a Meter needs before a sibling wraps to the next line. */
export const METER_MIN_REM = 16;

/** Side-by-side wrapper for two or more Meters — flex-wrap, each child
 *  `flex: 1 1 METER_MIN_REM`, so siblings sit in a row when there's room
 *  and stack when there isn't. No caller measures width. */
export function MeterGroup({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex flex-wrap" style={{ gap: 'var(--space-4)' }}>
      {React.Children.map(children, (child) =>
        child == null ? null : (
          <div style={{ flex: `1 1 ${METER_MIN_REM}rem`, minWidth: 0 }}>{child}</div>
        )
      )}
    </div>
  );
}

export interface MeterValueLabels {
  planned: string;
  actual: string;
  remaining: string;
}

export interface MeterRow {
  label: string;
  planned: number;
  actual: number;
  onClick?: () => void;
  ariaLabel?: string;
}

export interface MeterProps {
  label: string;
  planned: number;
  actual: number;
  values: MeterValueLabels;
  rows?: MeterRow[];
  compact?: boolean;
  /** Custom-widget accent (same union as WChart's `ChartSeries.tone`). Tints
   *  the filled bar in place of the planned/actual severity color. Unset =
   *  current behavior, unchanged. */
  tone?: ChartTone;
}

export function Meter({ label, planned, actual, values, rows, compact, tone }: MeterProps) {
  const pct = planned > 0 ? (actual / planned) * 100 : 0;
  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-col gap-1">
        <div className="flex items-center justify-between">
          <span className="text-sm font-semibold text-text">{label}</span>
        </div>
        <Bar pct={pct} height={compact ? 6 : 8} tone={tone} />
        <div className="flex items-center justify-between text-sm text-muted">
          <span>
            {values.actual} of {values.planned}
          </span>
          <span>{values.remaining}</span>
        </div>
      </div>
      {!compact && rows && rows.length > 0 && (
        <div className="flex flex-col gap-2">
          {rows.map((r) => {
            const rpct = r.planned > 0 ? (r.actual / r.planned) * 100 : 0;
            const content = (
              <>
                <span
                  className={cn('flex-shrink-0 truncate text-sm text-text')}
                  style={{ width: rem(96) }}
                >
                  {r.label}
                </span>
                <div className="flex-1">
                  <Bar pct={rpct} height={6} />
                </div>
              </>
            );
            if (r.onClick) {
              const onClick = r.onClick;
              return (
                <div
                  key={r.label}
                  role="button"
                  tabIndex={0}
                  aria-label={r.ariaLabel || r.label}
                  onClick={onClick}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      onClick();
                    }
                  }}
                  className={cn(
                    'flex items-center gap-2 rounded-md px-1 -mx-1 cursor-pointer hover:bg-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary'
                  )}
                  style={{ minHeight: rem(32) }}
                >
                  {content}
                </div>
              );
            }
            return (
              <div key={r.label} className="flex items-center gap-2">
                {content}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
